import { useTranslation } from 'react-i18next';

import { useServiceRouter } from '@common/components/ServiceRouter';
import { type IStudentGradeModel } from '@common/models/StudentGrades/StudentGrade';

import { getGradeWithTitle } from './getGradeWithLabel';

/**
 * Returns a formatted grade label based on the student's
 * grade information and whether it's a thematic grade.
 *
 * @param {IStudentGradeModel} grade - The student's grade data,
 * containing information about the grade, lesson, and other relevant details.
 * @returns {string} - A formatted string that includes the grade
 * label and, if applicable, a thematic grade label.
 *
 * This function first uses `getGradeWithTitle` to generate
 * the main grade label based on the student's grade data and
 * international settings. It then determines if the grade
 * is thematic (based on `gradebook_type`). If the grade is thematic,
 * it returns a label using the translation key 'gradeBook.
 * thematicGrade' and adds the grade label after a colon if present.
 * If not thematic, it simply returns the generated grade label.
 */

// eslint-disable-next-line import/prefer-default-export
export const useGetGradeWithLabelBasedOnThematic = (grade: IStudentGradeModel): string => {
	const { t } = useTranslation();
	const { checkService } = useServiceRouter();

	const gradeLabel = getGradeWithTitle(grade, checkService('international'));

	const isThematic = grade.gradebook_type === 'topic';

	const label = isThematic
		? t('gradeBook.thematicGrade', { label: `: ${gradeLabel}` })
		: gradeLabel;

	return label;
};
